import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

// Stock reserved against Transfer / Sales Orders — shared by the Transfer
// Orders and Sales Orders screens. A Transfer Order never blocks stock
// itself: stock is blocked when its Sales Order is raised, and blocked /
// unblocked only from the Sales Order (the Transfer Orders screen shows the
// state read-only). Backed by GET /api/stock/order-reservations
// (per order: reserved qty per line, how much of it is physically in stock
// first-come-first-served, and which other orders hold the same item) and
// GET /api/stock/availability (Physical − Reserved = Available per item).

export const ORDER_STOCK_STATUS = {
  reserved: { label: "Stock Reserved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partial: { label: "Blocked · Partly in Stock", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  awaiting: { label: "Blocked · Awaiting Stock", cls: "bg-red-50 text-red-700 border-red-200" },
  unblocked: { label: "Unblocked", cls: "bg-orange-50 text-orange-700 border-orange-300" },
  released: { label: "Released", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const fmt = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

// Roles allowed to Block / Unblock an order's stock — mirrors
// STOCK_BLOCK_ROLES (+ role aliases) in backend/server.py.
const STOCK_BLOCK_ROLES = new Set(["admin", "planner", "production_manager", "stores", "store_manager", "sales_crm"]);
export const canBlockStock = (role) => STOCK_BLOCK_ROLES.has(role);

// Per-order stock picture for a set of Transfer Order ids. `refreshKey` /
// `tick` force a reload (e.g. after the list reloads or a Block/Unblock).
export function useOrderStock(soIds, refreshKey, tick) {
  const [data, setData] = useState({});
  const key = [...new Set((soIds || []).filter(Boolean))].sort().join(",");
  useEffect(() => {
    if (!key) { setData({}); return; }
    let live = true;
    // POST, not GET: a list of a few hundred order ids is too long for a URL
    // (proxies reject query strings over ~8 KB with 414 URI Too Long).
    api.post("/stock/order-reservations", { so_ids: key.split(",") })
      .then(({ data: d }) => { if (live) setData(d || {}); })
      .catch(() => {});
    return () => { live = false; };
  }, [key, refreshKey, tick]);
  return data;
}

// Stock picture for the single order open in a detail view — loaded on its
// own so the panel never depends on the (much larger) list request.
export function useOneOrderStock(soId, tick) {
  const [state, setState] = useState({ info: null, error: null, loading: false });
  useEffect(() => {
    if (!soId) { setState({ info: null, error: null, loading: false }); return; }
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.post("/stock/order-reservations", { so_ids: [soId] })
      .then(({ data: d }) => {
        if (!live) return;
        const info = (d || {})[soId];
        setState({ info: info || null, error: info ? null : "No stock record found for this order's Transfer Order.", loading: false });
      })
      .catch((e) => {
        if (live) setState({ info: null, loading: false, error: formatApiError(e.response?.data?.detail) || e.message || "Could not load stock reservation" });
      });
    return () => { live = false; };
  }, [soId, tick]);
  return state;
}

// Live Physical / Reserved / Available for the items on an order form.
export function useStockAvailability(itemIds, excludeSoId) {
  const [data, setData] = useState({ items: {}, so_stock_check: "block" });
  const key = [...new Set((itemIds || []).filter(Boolean))].sort().join(",");
  useEffect(() => {
    if (!key) return;
    let live = true;
    api.get("/stock/availability", { params: { item_ids: key, ...(excludeSoId ? { exclude_so_id: excludeSoId } : {}) } })
      .then(({ data: d }) => { if (live) setData(d); })
      .catch(() => {});
    return () => { live = false; };
  }, [key, excludeSoId]);
  return data;
}

export function OrderStockBadge({ info, testid }) {
  if (!info) return null;
  const s = ORDER_STOCK_STATUS[info.status] || ORDER_STOCK_STATUS.released;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${s.cls}`}
      title={
        info.status === "released" ? "No stock held for this order"
          : info.status === "unblocked" ? "No stock is blocked for this order — it is blocked from the Sales Order"
            : `Reserved ${fmt(info.reserved_qty)} · in stock ${fmt(info.allocated_qty)}`
      }
      data-testid={testid}
    >
      {info.status === "unblocked" ? <LockOpen className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}{s.label}
    </span>
  );
}

// Block / Unblock switch with its confirmation dialog — used on the Sales
// Order (`csoId`) in the Stock Reservation panel and the main action row.
export function StockBlockButton({ info, csoId, onChanged, className = "", testid = "order-stock" }) {
  const [dialog, setDialog] = useState(null); // "block" | "unblock" | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!info || !info.can_toggle || !csoId) return null;
  const blocked = info.stock_blocked !== false;

  const submit = async () => {
    if (dialog === "unblock" && !reason.trim()) return toast.error("Please enter a reason for unblocking");
    setBusy(true);
    try {
      await api.post(`/customer-sales-orders/${csoId}/stock-${dialog}`, { reason: reason.trim() });
      toast.success(dialog === "unblock" ? "Stock unblocked — released to available stock" : "Stock blocked for this order");
      setDialog(null);
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update stock block");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Both options always visible: the current state is filled in (and
          not clickable); the other one switches the order over. */}
      <span className={`inline-flex rounded-md shadow-sm ${className}`} role="group" data-testid={`${testid}-toggle`}>
        <Button size="sm" variant="outline" disabled={blocked || busy}
          className={`rounded-r-none disabled:opacity-100 ${blocked
            ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-600 cursor-default"
            : "text-emerald-700 border-emerald-300 hover:bg-emerald-50"}`}
          title={blocked ? "Stock is currently blocked for this order" : "Reserve stock for this order"}
          onClick={() => { setReason(""); setDialog("block"); }} data-testid={`${testid}-block-btn`}>
          <Lock className="w-4 h-4 mr-1.5" />{blocked ? "Blocked" : "Block Stock"}
        </Button>
        <Button size="sm" variant="outline" disabled={!blocked || busy}
          className={`rounded-l-none -ml-px disabled:opacity-100 ${!blocked
            ? "bg-orange-500 text-white border-orange-500 hover:bg-orange-500 cursor-default"
            : "text-orange-700 border-orange-200 hover:bg-orange-50"}`}
          title={!blocked ? "Stock is currently not blocked for this order" : "Release this order's stock"}
          onClick={() => { setReason(""); setDialog("unblock"); }} data-testid={`${testid}-unblock-btn`}>
          <LockOpen className="w-4 h-4 mr-1.5" />{!blocked ? "Unblocked" : "Unblock Stock"}
        </Button>
      </span>
      <Dialog open={!!dialog} onOpenChange={(v) => { if (!v) setDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog === "unblock" ? "Unblock stock for this Sales Order?" : "Block stock for this Sales Order?"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {dialog === "unblock"
              ? "The quantity this order is holding goes back to available stock, so other orders can use it. The order itself stays open."
              : "The Sales Order's required (still to deliver) quantity is reserved. Whatever is in stock is held for it; any shortfall waits for stock (first come, first served) — other orders keep what they already hold."}
          </p>
          <div>
            <Label>Reason {dialog === "unblock" ? "*" : "(optional)"}</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-reason`} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className={dialog === "unblock" ? "bg-orange-600 hover:bg-orange-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid={`${testid}-confirm`}>
              {busy ? "Saving…" : dialog === "unblock" ? "Unblock Stock" : "Block Stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Detail-view panel: one row per order line, plus — on the Sales Order only —
// the Block / Unblock switch. `soId` = the Transfer Order holding the
// reservation; `csoId` = the Sales Order the switch acts on.
export function OrderStockPanel({ info, itemMap, soId, csoId, canToggle = false, onChanged, loading = false, error = null, testid = "order-stock-panel" }) {
  if (!info) {
    // Never render nothing — a silent blank hid the Block/Unblock controls.
    return (
      <div data-testid={testid}>
        <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Stock Reservation</div>
        {error ? (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid={`${testid}-error`}>
            <span>Could not load stock reservation: {error}</span>
            {onChanged && <button type="button" className="underline" onClick={onChanged}>Retry</button>}
          </div>
        ) : (
          <div className="text-xs text-slate-500">{loading ? "Loading stock…" : soId ? "Loading stock…" : "This order has no linked Transfer Order, so no stock is reserved against it."}</div>
        )}
      </div>
    );
  }
  const last = info.last_change;

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Stock Reservation</div>
        <OrderStockBadge info={info} testid={`${testid}-status`} />
        {canToggle && (
          <StockBlockButton info={info} csoId={csoId} onChanged={onChanged} className="ml-auto h-7" testid={testid} />
        )}
      </div>
      {last && (
        <div className="text-[11px] text-slate-500 mb-2" data-testid={`${testid}-last-change`}>
          {last.action === "unblock" ? "Unblocked" : "Blocked"} by <span className="mono">{last.by}</span> on {new Date(last.at).toLocaleString()}
          {last.reason && <> — "{last.reason}"</>}
        </div>
      )}
      {info.status === "unblocked" ? (
        <div className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
          Stock is <strong>unblocked</strong> for this order — its quantity is not reserved and is available to other orders.
          {canToggle ? " Use Block Stock to reserve it." : " Stock is blocked from the Sales Order once it is raised."}
        </div>
      ) : info.lines.length === 0 ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          No stock is held for this order — it is closed, cancelled or fully delivered, so its quantity has been released back to available stock.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                <th className="py-2 px-3 whitespace-nowrap">Part No</th>
                <th className="py-2 px-3 text-right">Ordered</th>
                <th className="py-2 px-3 text-right">Delivered</th>
                <th className="py-2 px-3 text-right bg-purple-50/60">Reserved (this order)</th>
                <th className="py-2 px-3 text-right">In Stock</th>
                <th className="py-2 px-3 text-right">Awaiting</th>
                <th className="py-2 px-3 text-right">Physical Stock</th>
                <th className="py-2 px-3 text-right">Total Reserved</th>
                <th className="py-2 px-3 text-right bg-sky-50/60">Available</th>
                <th className="py-2 px-3 whitespace-nowrap">Also Reserved For</th>
              </tr>
            </thead>
            <tbody>
              {info.lines.map((l) => (
                <tr key={`${l.item_id}-${l.line_no}`} className="border-t border-slate-100" data-testid={`${testid}-line-${l.line_no}`}>
                  <td className="py-2 px-3 mono text-xs whitespace-nowrap">{itemMap?.[l.item_id]?.code || l.item_id}</td>
                  <td className="py-2 px-3 mono text-right">{fmt(l.ordered_qty)}</td>
                  <td className="py-2 px-3 mono text-right">{fmt(l.delivered_qty)}</td>
                  <td className="py-2 px-3 mono text-right bg-purple-50/40 font-semibold">{fmt(l.reserved_qty)}</td>
                  <td className="py-2 px-3 mono text-right text-emerald-700">{fmt(l.allocated_qty)}</td>
                  <td className={`py-2 px-3 mono text-right ${l.awaiting_qty > 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>{fmt(l.awaiting_qty)}</td>
                  <td className="py-2 px-3 mono text-right">{fmt(l.physical_stock)}</td>
                  <td className="py-2 px-3 mono text-right">{fmt(l.item_reserved_qty)}</td>
                  <td className="py-2 px-3 mono text-right bg-sky-50/40">{fmt(l.item_available_qty)}</td>
                  <td className="py-2 px-3 text-[11px] text-purple-800 whitespace-nowrap">
                    {l.other_orders.length === 0 ? <span className="text-slate-400">—</span> : l.other_orders.slice(0, 3).map((o) => (
                      <div key={o.order_no} title={o.sales_order_codes?.join(", ") || undefined}>
                        <span className="mono">{o.order_no}</span>
                        {o.customer_name && <span className="text-slate-600"> · {o.customer_name}</span>}: {fmt(o.reserved_qty)}
                      </div>
                    ))}
                    {l.other_orders.length > 3 && <div className="text-slate-500">+{l.other_orders.length - 3} more</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11px] text-slate-400 mt-1">
            Available = Physical Stock − Total Reserved across all open orders. Stock is allocated to orders first-come-first-served; "Awaiting" still has to be produced or purchased.
          </div>
        </div>
      )}
    </div>
  );
}

// Under a qty input on an order form.
export function StockHint({ pos, requested, mode, testid }) {
  if (!pos || mode === "off") return null;
  const over = mode !== "info" && Number(requested || 0) > pos.available_qty + 1e-9;
  const orders = (pos.reservations || [])
    .map((r) => `${r.order_no}${r.customer_name ? ` (${r.customer_name})` : ""}: ${r.reserved_qty}`)
    .join("\n");
  return (
    <div
      className={`mt-1 text-[10px] leading-tight whitespace-nowrap ${over ? "text-red-600 font-semibold" : "text-slate-500"}`}
      title={`Physical ${pos.physical_stock} − Reserved ${pos.reserved_qty} = Available ${pos.available_qty}${orders ? `\nReserved against:\n${orders}` : ""}`}
      data-testid={testid}
    >
      Avail {fmt(pos.available_qty)}
      <span className="text-slate-400"> · Phy {fmt(pos.physical_stock)} · Rsv {fmt(pos.reserved_qty)}</span>
      {over && mode !== "info" && (
        <div>
          {mode === "reject"
            ? "Exceeds available — order will be rejected"
            : `Exceeds available — will be blocked, awaiting ${fmt(Number(requested || 0) - pos.available_qty)}`}
        </div>
      )}
    </div>
  );
}
